// ===========================================
// API Service - Centralized API Client
// ===========================================
// Supports both legacy tokens and new access/refresh token flow

import axios from 'axios';

// Token storage keys
const TOKEN_KEY = 'devpad_token';           // Legacy/backwards compatible
const ACCESS_TOKEN_KEY = 'devpad_access';   // New: short-lived access token
const REFRESH_TOKEN_KEY = 'devpad_refresh'; // New: long-lived refresh token

// Track if we're currently refreshing to prevent multiple refresh calls
let isRefreshing = false;
let failedQueue = [];

// ===========================================
// Session Expiration Notification
// ===========================================
// Dispatches a custom event that the app can listen to for session expiration
// This allows graceful handling with user notification before redirect
const notifySessionExpired = (reason = 'Session expired') => {
  // Dispatch custom event for app-level handling (e.g., toast notifications)
  window.dispatchEvent(new CustomEvent('devpad:session-expired', {
    detail: { reason, timestamp: new Date().toISOString() }
  }));
  
  // Also log to console for debugging
  console.warn('[API] Session expired:', reason);
};

// Process queued requests after token refresh
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json'
  }
});

// ===========================================
// Request Interceptor - Attach auth token
// ===========================================
// Checks both localStorage (remembered) and sessionStorage (temporary) for tokens
// Prefers access token, falls back to legacy token
api.interceptors.request.use(
  (config) => {
    // Check localStorage first (remembered sessions), then sessionStorage (temporary sessions)
    // This supports the "Remember Me" feature where users can choose persistent vs session storage
    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY) || sessionStorage.getItem(ACCESS_TOKEN_KEY);
    const legacyToken = localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
    const token = accessToken || legacyToken;
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ===========================================
// Response Interceptor - Handle errors globally
// ===========================================
// Implements automatic token refresh on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Handle 401 - Unauthorized (token expired or invalid)
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Check if we have a refresh token to try (check both storages)
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY) || sessionStorage.getItem(REFRESH_TOKEN_KEY);
      
      if (refreshToken && !originalRequest.url?.includes('/auth/refresh')) {
        if (isRefreshing) {
          // Queue this request until refresh completes
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          }).catch(err => {
            return Promise.reject(err);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          // Attempt to refresh the token
          const response = await axios.post(
            `${api.defaults.baseURL}/auth/refresh`,
            { refreshToken }
          );
          
          const { accessToken, refreshToken: newRefreshToken } = response.data.data;
          
          // Determine which storage to use based on remember preference
          const rememberMe = localStorage.getItem('devpad_remember') === 'true';
          const storage = rememberMe ? localStorage : sessionStorage;
          
          // Store new tokens in the appropriate storage
          storage.setItem(ACCESS_TOKEN_KEY, accessToken);
          storage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
          
          // Update legacy token for backwards compatibility
          storage.setItem(TOKEN_KEY, accessToken);
          
          // Process queued requests
          processQueue(null, accessToken);
          
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed - notify user, clear tokens, and redirect
          processQueue(refreshError, null);
          notifySessionExpired('Your session has expired. Please log in again.');
          clearTokens();
          
          // Small delay to allow notification to be seen/handled
          if (window.location.pathname !== '/login') {
            setTimeout(() => {
              window.location.href = '/login';
            }, 100);
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      } else {
        // No refresh token - notify user, clear and redirect
        notifySessionExpired('Your session has expired. Please log in again.');
        clearTokens();
        
        // Small delay to allow notification to be seen/handled
        if (window.location.pathname !== '/login') {
          setTimeout(() => {
            window.location.href = '/login';
          }, 100);
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// ===========================================
// Token Management Helpers
// ===========================================
// Clears tokens from both localStorage and sessionStorage to support "Remember Me" feature
const clearTokens = () => {
  // Clear from localStorage (persistent/remembered sessions)
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem('devpad_user');
  localStorage.removeItem('devpad_remember');
  // Clear from sessionStorage (temporary sessions)
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  sessionStorage.removeItem('devpad_user');
};

// Store tokens from auth response (handles both legacy and new format)
// Uses the active storage based on "Remember Me" preference
export const storeAuthTokens = (data) => {
  // Determine which storage to use based on remember preference
  const rememberMe = localStorage.getItem('devpad_remember') === 'true';
  const storage = rememberMe ? localStorage : sessionStorage;
  
  // Store legacy token for backwards compatibility
  if (data.token) {
    storage.setItem(TOKEN_KEY, data.token);
  }
  // Store new tokens if available
  if (data.accessToken) {
    storage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
  }
  if (data.refreshToken) {
    storage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
  }
  // Store user data
  if (data.user) {
    storage.setItem('devpad_user', JSON.stringify(data.user));
  }
};

// Logout helper - clears all tokens
export const logout = () => {
  clearTokens();
};

// ===========================================
// Auth API
// ===========================================
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  googleAuth: (data) => api.post('/auth/google', data),  // Google OAuth login
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/password', data),
  deleteAccount: (data) => api.delete('/auth/account', { data }),
  
  // Refresh token endpoint - get new access token
  refreshToken: (refreshToken) => api.post('/auth/refresh', { refreshToken }),
  
  // Password verification for sensitive operations (without creating new token)
  verifyPassword: (data) => api.post('/auth/verify-password', data),
  
  // Global Subscriptions - shared dev costs (Cursor, ChatGPT, Vercel, etc.)
  getSubscriptions: () => api.get('/auth/subscriptions'),
  addSubscription: (data) => api.post('/auth/subscriptions', data),
  updateSubscription: (id, data) => api.put(`/auth/subscriptions/${id}`, data),
  deleteSubscription: (id) => api.delete(`/auth/subscriptions/${id}`),
  
  // Cursor Usage - AI Dev Token variable costs from billing CSV exports
  // Historical storage with date range filtering support
  uploadCursorUsage: (csvData) => api.post('/auth/cursor-usage', { csvData }),
  getCursorUsage: (params) => api.get('/auth/cursor-usage', { params }),
  
  // Cursor Activity Calendar - Daily usage for GitHub-style heatmap (past year)
  getCursorActivityCalendar: () => api.get('/auth/cursor-activity')
};

// ===========================================
// Projects API
// ===========================================
export const projectAPI = {
  // CRUD operations
  list: (params) => api.get('/projects', { params }),
  get: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  permanentDelete: (id) => api.delete(`/projects/${id}/permanent`),
  
  // Focus tracking
  focus: (id, duration) => api.post(`/projects/${id}/focus`, duration !== undefined ? { duration } : {}),
  
  // Services
  addService: (projectId, data) => api.post(`/projects/${projectId}/services`, data),
  updateService: (projectId, serviceId, data) => 
    api.put(`/projects/${projectId}/services/${serviceId}`, data),
  deleteService: (projectId, serviceId) => 
    api.delete(`/projects/${projectId}/services/${serviceId}`),
  getServiceCredentials: (projectId, serviceId) => 
    api.get(`/projects/${projectId}/services/${serviceId}/credentials`),
  
  // Environment variables
  getEnvVariables: (projectId, environment) => 
    api.get(`/projects/${projectId}/env`, { params: { environment } }),
  addEnvVariable: (projectId, data) => api.post(`/projects/${projectId}/env`, data),
  updateEnvVariable: (projectId, envId, data) => 
    api.put(`/projects/${projectId}/env/${envId}`, data),
  deleteEnvVariable: (projectId, envId) => 
    api.delete(`/projects/${projectId}/env/${envId}`),
  
  // Roadmap
  addMilestone: (projectId, data) => api.post(`/projects/${projectId}/roadmap`, data),
  updateMilestone: (projectId, milestoneId, data) => 
    api.put(`/projects/${projectId}/roadmap/${milestoneId}`, data),
  deleteMilestone: (projectId, milestoneId) => 
    api.delete(`/projects/${projectId}/roadmap/${milestoneId}`),
  
  // Files - Upload screenshots, documents, and project assets
  getFiles: (projectId) => api.get(`/projects/${projectId}/files`),
  getFile: (projectId, fileId) => api.get(`/projects/${projectId}/files/${fileId}`),
  uploadFile: (projectId, formData) => api.post(`/projects/${projectId}/files`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteFile: (projectId, fileId) => api.delete(`/projects/${projectId}/files/${fileId}`),
  
  // Tech Stack - Auto-categorize technologies based on name patterns
  categorizeTechStack: (projectId) => api.post(`/projects/${projectId}/categorize-techstack`)
};

// ===========================================
// Analytics API (Phase 5) - Investment Portfolio
// ===========================================
export const analyticsAPI = {
  // Unified endpoint - fetches all analytics data in one request (recommended)
  getAll: () => api.get('/analytics/all'),
  // Individual endpoints (kept for backwards compatibility / specific use cases)
  getOverview: () => api.get('/analytics/overview'),
  getByProject: () => api.get('/analytics/by-project'),
  getByServiceType: () => api.get('/analytics/by-service-type'),
  getProjectCosts: (projectId) => api.get(`/analytics/project/${projectId}`),
  getARRDistribution: () => api.get('/analytics/arr-distribution')
};

// ===========================================
// AI API (Phase 6)
// ===========================================
export const aiAPI = {
  chat: (data) => api.post('/ai/chat', data),
  analyzeCosts: () => api.post('/ai/analyze-costs'),
  getProjectInsights: (projectId) => api.post(`/ai/project-insights/${projectId}`),
  getSuggestions: () => api.get('/ai/suggestions'),
  
  // README to Idea - Generate Big Rock section content from README.md
  // Uses xAI to intelligently analyze README and fill Idea Workshop sections
  readmeToIdea: (projectId, section) => api.post('/ai/readme-to-idea', { projectId, section })
};

// ===========================================
// Dev Prompts API - AI IDE Prompt Management
// ===========================================
// Enables developers to save, organize, and reuse prompts for Cursor, Copilot, etc.
export const promptsAPI = {
  // CRUD operations
  list: (params) => api.get('/prompts', { params }),
  get: (id) => api.get(`/prompts/${id}`),
  create: (data) => api.post('/prompts', data),
  update: (id, data) => api.put(`/prompts/${id}`, data),
  delete: (id) => api.delete(`/prompts/${id}`),
  
  // Hybrid search (full-text + vector when VoyageAI configured)
  // Uses Reciprocal Rank Fusion to combine results from both search types
  search: (query, options = {}) => api.get('/prompts/search', { 
    params: { q: query, ...options } 
  }),
  
  // Usage tracking - call when user copies a prompt
  recordCopy: (id) => api.post(`/prompts/${id}/copy`),
  
  // Favorite management
  toggleFavorite: (id) => api.post(`/prompts/${id}/favorite`),
  
  // Duplicate a prompt
  duplicate: (id) => api.post(`/prompts/${id}/duplicate`),
  
  // Version management
  getVersions: (id) => api.get(`/prompts/${id}/versions`),
  restoreVersion: (id, version) => api.post(`/prompts/${id}/restore/${version}`),
  
  // Reorder prompts (drag & drop)
  reorder: (orderedIds) => api.put('/prompts/reorder', { orderedIds }),
  
  // ===========================================
  // Tag Management - Smart drill-down filtering
  // ===========================================
  // Get all tags with counts for tag cloud/filter UI
  getTagStats: () => api.get('/prompts/tags/stats'),
  
  // Search tags for autocomplete (min 2 chars)
  searchTags: (query) => api.get(`/prompts/tags/search?q=${encodeURIComponent(query)}`),
  
  // Get list of all recognized tech tags
  getAvailableTags: () => api.get('/prompts/tags/available'),
  
  // Get prompts by tag with pagination
  getByTag: (tag, page = 1, limit = 20) => 
    api.get(`/prompts/by-tag/${encodeURIComponent(tag)}?page=${page}&limit=${limit}`),
  
  // ===========================================
  // AI Tag Categories - Dynamic Filtering
  // ===========================================
  // Get AI-generated tag categories for organic sidebar filtering
  // Categories grow as users create prompts and AI analyzes content
  getAITagCategories: () => api.get('/prompts/ai-categories'),
  
  // ===========================================
  // Similar Prompts - Semantic Similarity via Embeddings
  // ===========================================
  // Find prompts semantically similar to a given prompt
  // Uses VoyageAI embeddings for high-quality similarity matching
  getSimilar: (id, options = {}) => api.get(`/embeddings/similar/prompts/${id}`, { 
    params: options 
  })
};

// ===========================================
// Prompt Expert API - AI Prompt Engineering Assistant
// ===========================================
// Specialized AI chat for building and optimizing coding prompts
export const promptExpertAPI = {
  // Conversational chat with prompt expert
  chat: (data) => api.post('/prompts/expert/chat', data),
  
  // Optimize an existing prompt
  optimize: (data) => api.post('/prompts/expert/optimize', data),
  
  // Generate new prompt from requirements
  generate: (data) => api.post('/prompts/expert/generate', data),
  
  // Get starter templates by category
  getTemplates: (category) => api.get(`/prompts/expert/templates/${category}`),
  
  // Generate metadata (description & tags) from prompt content
  // Used when applying prompts from Prompt Builder to auto-fill form fields
  generateMetadata: (data) => api.post('/prompts/expert/metadata', data),
  
  // Score prompt quality against Task Brief framework criteria
  // Returns { score, maxScore, grade, results, suggestions }
  scoreQuality: (data) => api.post('/prompts/expert/score', data),
  
  // Get context attachment suggestions based on role + intent
  // Returns { files, folders, tips, recommendedMode, modeRationale }
  getContextSuggestions: ({ role, intent }) => 
    api.get(`/prompts/expert/context-suggestions?role=${role}&intent=${intent}`),
  
  // Get all intent definitions with mode recommendations
  getIntents: () => api.get('/prompts/expert/intents')
};

// ===========================================
// Chat API - Global Dev Chat
// ===========================================
// Universal chat for devs to talk shop across rooms
export const chatAPI = {
  // Get messages for a specific room
  getMessages: (room, params) => api.get(`/chat/${room}`, { params }),
  
  // Send a new message
  sendMessage: (data) => api.post('/chat', data),
  
  // Delete own message
  deleteMessage: (id) => api.delete(`/chat/${id}`),
  
  // Get unread message counts for all rooms
  getUnreadCounts: () => api.get('/chat/unread/counts'),
  
  // Mark a specific room as read (updates user's last read timestamp)
  markRoomAsRead: (room) => api.post(`/chat/read/${room}`)
};

// ===========================================
// Changelog API - Feature Update Tracking
// ===========================================
// Manage changelog entries for weekly AI-generated news updates
export const changelogAPI = {
  // Get all changelog entries (optionally filter by announced status)
  getAll: (params) => api.get('/changelog', { params }),
  
  // Add a new changelog entry
  add: (data) => api.post('/changelog', data),
  
  // Manually trigger weekly news update
  triggerWeekly: () => api.post('/changelog/trigger-weekly'),
  
  // Delete a changelog entry
  delete: (id) => api.delete(`/changelog/${id}`)
};

// ===========================================
// Notes API - Dev Notes Knowledge Notebook
// ===========================================
// Save learnings, AI insights, and developer knowledge
// Supports topics, tags, pinning, and archiving
export const notesAPI = {
  // CRUD operations
  list: (params) => api.get('/notes', { params }),
  get: (id) => api.get(`/notes/${id}`),
  create: (data) => api.post('/notes', data),
  update: (id, data) => api.put(`/notes/${id}`, data),
  delete: (id) => api.delete(`/notes/${id}`),
  
  // Hybrid search (full-text + vector when VoyageAI configured)
  // Uses Reciprocal Rank Fusion to combine results from both search types
  search: (query, options = {}) => api.get('/notes/search', { 
    params: { q: query, ...options } 
  }),
  
  // Note actions
  togglePin: (id) => api.post(`/notes/${id}/pin`),
  toggleArchive: (id) => api.post(`/notes/${id}/archive`),
  
  // Reorder notes (for custom sorting)
  reorder: (orderedIds) => api.put('/notes/reorder', { orderedIds }),
  
  // Statistics
  getTopicStats: () => api.get('/notes/stats/topics'),
  
  // AI Tag Categories - Dynamic categories that grow organically
  // Returns aggregated AI-generated tags grouped by type (outcomes, patterns, technologies)
  getAITagCategories: () => api.get('/notes/ai-categories')
};

export default api;

